/**
 * Buy X Get Y - Save 10%
 * Simplified version - cart.js handles insert/update/remove
 */

if (!customElements.get('buy-x-get-y')) {
  class BuyXGetY extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.xProductIds = (this.dataset.xProductIds || '').split(',').filter(Boolean);
      this.discountMode = this.dataset.discountMode || 'n_to_one';
    }

    connectedCallback() {
      this.setupButtons();
      this.updateTexts();
    }

    setupButtons() {
      this.querySelectorAll('[data-add-bxgy-item]').forEach((btn) => {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        newBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleAddToCart(e);
        });
      });
    }

    async handleAddToCart(event) {
      const button = event.currentTarget;
      if (button.disabled) return;

      const card = button.closest('.bxgy-card');
      if (!card) return;

      const variantId = card.dataset.variantId;
      const productTitle = card.dataset.productTitle;

      button.classList.add('loading');
      button.disabled = true;

      try {
        // Check if discount is available based on mode
        const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartRes.json();
        const counts = this.countXY(cart);
        const hasDiscount = this.checkDiscount(counts);

        const cartItem = {
          id: parseInt(variantId),
          quantity: 1,
        };

        if (hasDiscount) {
          cartItem.properties = { _bxgy_item: 'true' };
        }

        // Add to cart
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [cartItem] }),
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Show toast
        this.showToast(hasDiscount
          ? `${productTitle} added with 10% off!`
          : `${productTitle} added to cart!`);

        // Use theme's pubsub to trigger cart update
        if (window.FoxTheme?.pubsub?.publish) {
          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
          );

          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const cartData = await cartResponse.json();

          const sectionsResponse = await fetch(`${window.Shopify.routes.root}?sections=${sectionsToBundle.join(',')}`);
          const sectionsData = await sectionsResponse.json();
          cartData.sections = sectionsData;

          window.FoxTheme.pubsub.publish(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            { cart: cartData }
          );
        } else {
          // Fallback
          document.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: true }
          }));
        }

      } catch (error) {
        console.error('BXGY Add Error:', error);
        this.showToast('Could not add item', 'error');
      } finally {
        button.classList.remove('loading');
        button.disabled = false;
      }
    }

    countXY(cart) {
      let xCount = 0;
      let yCount = 0;

      cart.items.forEach(item => {
        if (item.properties && item.properties._bxgy_item === 'true') {
          yCount += item.quantity;
        } else if (this.xProductIds.includes(String(item.product_id))) {
          xCount += item.quantity;
        }
      });

      return { x: xCount, y: yCount };
    }

    checkDiscount(counts) {
      if (this.discountMode === 'n_to_one') {
        // N to 1: Only 1 Y gets discount, regardless of X count
        return counts.x >= 1 && counts.y === 0;
      } else {
        // 1 to 1: Each X unlocks 1 Y discount
        return counts.x > counts.y;
      }
    }

    checkAlreadyUsed(counts) {
      if (this.discountMode === 'n_to_one') {
        return counts.y >= 1;
      }
      return false;
    }

    async updateTexts() {
      try {
        const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartRes.json();
        const counts = this.countXY(cart);
        const hasDiscount = this.checkDiscount(counts);
        const alreadyUsed = this.checkAlreadyUsed(counts);

        const subtitle = this.querySelector('.buy-x-get-y__header p');
        if (subtitle) {
          if (hasDiscount) {
            subtitle.textContent = "You've unlocked 10% off! Pick a product below";
          } else if (alreadyUsed) {
            subtitle.textContent = "You've received your discount!";
          } else {
            subtitle.textContent = 'Add more qualifying items to unlock another 10% off';
          }
        }

        // Button text based on discount availability
        this.querySelectorAll('.bxgy-card__add-btn-text').forEach(btnText => {
          btnText.textContent = hasDiscount ? 'Add -10%' : 'Add to Cart';
        });

        this.querySelectorAll('.bxgy-card__price').forEach(priceEl => {
          const originalPriceEl = priceEl.querySelector('.bxgy-card__price-original');
          const salePriceEl = priceEl.querySelector('.bxgy-card__price-sale');
          const originalPrice = priceEl.dataset.originalPrice;
          const salePrice = priceEl.dataset.salePrice;

          if (originalPriceEl && salePriceEl) {
            if (hasDiscount) {
              originalPriceEl.style.display = '';
              salePriceEl.textContent = salePrice;
              salePriceEl.style.color = '#059669';
            } else {
              originalPriceEl.style.display = 'none';
              salePriceEl.textContent = originalPrice;
              salePriceEl.style.color = '';
            }
          }
        });

        this.dataset.discountAvailable = hasDiscount;
        this.dataset.alreadyUsed = alreadyUsed;
      } catch (e) {
        // Silent fail
      }
    }

    showToast(message, type = 'success') {
      if (window.FoxTheme?.Notification?.show) {
        window.FoxTheme.Notification.show(message, type);
        return;
      }

      document.querySelector('.bxgy-toast')?.remove();
      const toast = document.createElement('div');
      toast.className = `bxgy-toast bxgy-toast--${type}`;
      toast.textContent = message;
      document.body.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('bxgy-toast--visible'));
      setTimeout(() => {
        toast.classList.remove('bxgy-toast--visible');
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    }
  }

  customElements.define('buy-x-get-y', BuyXGetY);
}

/**
 * Simple event listener to update texts when cart changes
 */
(function() {
  const updateBXGY = () => {
    const bxgy = document.querySelector('buy-x-get-y');
    if (bxgy) {
      bxgy.updateTexts();
    }
  };

  // Listen for cart updates
  document.addEventListener('cart:updated', updateBXGY);

  // Subscribe to FoxTheme pubsub
  const subscribeToPubsub = () => {
    if (window.FoxTheme?.pubsub?.subscribe) {
      window.FoxTheme.pubsub.subscribe(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        () => setTimeout(updateBXGY, 100)
      );
    } else {
      setTimeout(subscribeToPubsub, 500);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', subscribeToPubsub);
  } else {
    subscribeToPubsub();
  }
})();
